import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { WikiService } from '../../lib/api/wiki.service'
import {
	GetWikisDataResponse,
	WikiDataProps,
	WikiData,
	TopicProps,
	GetTopicsResponse,
} from '../../lib/api/types/wiki.interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { handleApiError } from '../../lib/utils/handleApiError'

export const useWikiItems = ({ offset, limit }: { offset?: number; limit?: number },
	options?: Partial<QueryObserverOptions<GetWikisDataResponse>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetWikisDataResponse>({
		queryKey: ['wikis', offset, limit],
		queryFn: () => WikiService.getWikis({ offset, limit }),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useWikiItem = (
	id: string,
	refetchOnWindowFocus: boolean = false,
	options?: Partial<QueryObserverOptions<WikiData>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<WikiData>({
		queryKey: ['wiki', id],
		queryFn: () => WikiService.getWiki({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		refetchOnWindowFocus,
		...options,
	})
}

export const useAddWiki = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['addWiki'],
		mutationFn: ({ data }: { data: WikiDataProps }) =>
			WikiService.addWiki({ data }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				message: 'Wiki page added successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
			queryClient.invalidateQueries({ queryKey: ['topics'] })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const res = await mutation.mutateAsync(...params)
			return res
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateWiki = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateWiki'],
		mutationFn: ({ data }: { data: WikiData }) =>
			WikiService.updateWiki({ data }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, variables) => {
			toastCaller({
				message: 'Wiki page updated successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
			queryClient.invalidateQueries({ queryKey: ['topics'] })
			queryClient.invalidateQueries({
				queryKey: ['wiki', variables.data.id],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteWiki = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['deleteWiki'],
		mutationFn: ({ id }: { id: string }) =>
			WikiService.deleteWiki({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				message: 'Wiki page deleted successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
			queryClient.invalidateQueries({ queryKey: ['topics'] })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useTopics = ({ startDate,
		endDate,
		timezoneOffset,
		query,
	}: {
		startDate?: string
		endDate?: string
		timezoneOffset?: number | null
		query?: string
	},
	options?: Partial<QueryObserverOptions<GetTopicsResponse>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetTopicsResponse>({
		queryKey: [
			'topics',
			startDate,
			endDate,
			timezoneOffset,
			query,
		],
		queryFn: () =>
			WikiService.getTopics({ startDate,
				endDate,
				timezoneOffset,
				query,
			}),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useCreateTopic = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['createTopic'],
		mutationFn: ({ data,
		}: {
			data: TopicProps
		}) => WikiService.createTopic({ data }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				message: 'Topic created successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['topics'] })
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const res = await mutation.mutateAsync(...params)
			return res
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateTopic = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateTopic'],
		mutationFn: ({ id,
			data,
		}: {
			id: string
			data: TopicProps
		}) => WikiService.updateTopic({ id, data }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				message: 'Topic updated successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['topics'] })
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteTopic = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['deleteTopic'],
		mutationFn: ({ id }: { id: string }) =>
			WikiService.deleteTopic({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			toastCaller({
				message: 'Topic deleted successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({ queryKey: ['topics'] })
			queryClient.invalidateQueries({ queryKey: ['wikis'] })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGetWikiPermissions = ({ id }: { id: string },
	options?: Partial<QueryObserverOptions<any>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<any>({
		queryKey: ['wikiPermissions', id],
		queryFn: () => WikiService.getWikiPermissions({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useUpdateWikiPermissions = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updateWikiPermissions'],
		mutationFn: ({ id,
			data,
		}: {
			id: string
			data: { userId: string; level: number }[]
		}) => WikiService.updateWikiPermissions({ id, data }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, variables) => {
			toastCaller({
				message: 'Permissions updated successfully',
				type: 'success',
			})
			queryClient.invalidateQueries({
				queryKey: ['wikiPermissions', variables.id],
			})
		},
	})

	return mutation
}
